import { type BedKey, RESIDENT_STATE } from "@packages/core";
import React, { useMemo } from "react";
import { LuChevronRight, LuMegaphone, LuMegaphoneOff } from "react-icons/lu";
import { Merge } from "type-fest";

import {
  fromFallRiskToColor,
  useBedQuery,
  useEasy,
  useLatestFallRiskEntryQuery,
  useLatestStatusQuery,
  useNow,
  useResidentImageQuery,
  useResidentQuery,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

import { PaperStyle, TypographyStyle } from "../common";
import {
  AvatarPlaceholder,
  GotUpPictogram,
  SleepingHeavilyPictogram,
  SleepingLightlyPictogram,
  SleepingPictogram,
  WakingPictogram,
} from "../images";

import { FallRiskDisplay } from "./fall-risk-display";

export type BedCardProperties = Merge<
  React.ComponentProps<"div">,
  { bedKey?: BedKey | false | null }
>;

export function BedCard({ className, bedKey, ...properties }: BedCardProperties) {
  const translate = useTranslate();
  const now = useNow();

  const bedQuery = useBedQuery(bedKey);
  const [easy] = useEasy();

  const latestFallRiskEntryQuery = useLatestFallRiskEntryQuery(bedQuery.data?.userKey);
  const residentQuery = useResidentQuery(bedQuery.data?.userKey);
  const residentImageQuery = useResidentImageQuery(bedQuery.data?.userKey);
  const { data: status } = useLatestStatusQuery(bedQuery.data?.userKey);

  const alerted = status ? status.alerted && !status.confirmed : false;
  const fallRisk = latestFallRiskEntryQuery.data?.fallRisk;

  const pictogram = useMemo(
    function () {
      switch (status?.state) {
        case RESIDENT_STATE.GOT_UP:
        case RESIDENT_STATE.WANDERING:
          return (
            <GotUpPictogram
              className={css({ height: "100%", width: "100%" })}
              style={{ color: fromFallRiskToColor(fallRisk) }}
            />
          );
        case RESIDENT_STATE.SLEEPING:
          return (
            <SleepingPictogram
              className={css({ color: "#747476", height: "100%", width: "100%" })}
            />
          );
        case RESIDENT_STATE.SLEEPING_HEAVILY:
          return (
            <SleepingHeavilyPictogram
              className={css({ color: "#747476", height: "100%", width: "100%" })}
            />
          );
        case RESIDENT_STATE.SLEEPING_LIGHTLY:
          return (
            <SleepingLightlyPictogram
              className={css({ color: "#747476", height: "100%", width: "100%" })}
            />
          );
        case RESIDENT_STATE.WAKING:
          return (
            <WakingPictogram className={css({ color: "#747476", height: "100%", width: "100%" })} />
          );
        default:
          return null;
      }
    },
    [fallRisk, status?.state],
  );

  return (
    <div
      {...properties}
      className={cx(
        css(PaperStyle.raw({ borderRadius: "12px" }), {
          borderStyle: "solid",
          borderWidth: "2px",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          columnGap: "8px",
          paddingX: "16px",
          paddingY: "12px",
          rowGap: "12px",
        }),
        className,
      )}
      style={{
        borderColor: alerted
          ? status?.state === RESIDENT_STATE.WANDERING
            ? "#18181B"
            : fromFallRiskToColor(fallRisk)
          : "#FFFFFF",
      }}
    >
      {residentImageQuery.data ? (
        <img
          alt=""
          className={css({
            aspectRatio: "1 / 1",
            borderRadius: "12px",
            display: "block",
            height: "96px",
          })}
          src={residentImageQuery.data}
        />
      ) : (
        <AvatarPlaceholder
          className={css({
            aspectRatio: "1 / 1",
            borderRadius: "12px",
            display: "block",
            height: "96px",
          })}
        />
      )}
      <div className={css({ minWidth: 0 })}>
        <p
          className={TypographyStyle({
            color: "primary",
            ellipsis: true,
            fontSize: "16px",
            fontWeight: 500,
            lineHeight: 1,
          })}
        >
          {residentQuery.data?.displayName || residentQuery.data?.name || "\u00A0"}
        </p>
        <p
          className={TypographyStyle({
            color: "primary/60",
            ellipsis: true,
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: 1,
            marginTop: "6px",
          })}
        >
          {bedQuery.data?.roomName || "\u00A0"}
          {bedQuery.data?.bedName ? " - " + bedQuery.data.bedName : null}
        </p>
        {status?.state !== RESIDENT_STATE.PROVIDER_ERROR &&
        status?.state !== RESIDENT_STATE.SENSOR_ERROR ? (
          <FallRiskDisplay className={css({ marginTop: "12px" })} fallRisk={fallRisk} />
        ) : (
          <p
            className={TypographyStyle({
              ellipsis: true,
              fontSize: "24px",
              fontWeight: 700,
              lineHeight: 1,
              marginTop: "12px",
            })}
          >
            {translate("%ResidentState." + (status?.state ?? RESIDENT_STATE.UNKNOWN) + ".2%")}
          </p>
        )}

        <div
          className={css({
            display: "flex",
            gap: "4px",
            marginTop: "6px",
            '&[data-display-none="true"]': { display: "none" },
          })}
          data-display-none={!residentQuery.data}
        >
          {([RESIDENT_STATE.GOT_UP, RESIDENT_STATE.WANDERING] as const).map(
            function (residentState) {
              const enabled = residentQuery.data?.alertSettings[residentState].enabled;
              return (
                <div
                  className={cx(
                    css(TypographyStyle.raw({ fontSize: "12px", fontWeight: 500, lineHeight: 1 }), {
                      borderRadius: "2px",
                      display: "flex",
                      gap: "1px",
                      padding: "2px",
                    }),
                    enabled
                      ? css({ backgroundColor: "#0D9488", color: "white" })
                      : css({ backgroundColor: "#F4F4F5", color: "#71717A" }),
                  )}
                  key={residentState}
                >
                  {enabled ? <LuMegaphone /> : <LuMegaphoneOff />}
                  <p>{translate("%ResidentState." + residentState + ".3%")}</p>
                </div>
              );
            },
          )}
        </div>
      </div>

      <div>
        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "4px",
            justifyContent: "flex-end",
          })}
        >
          <span
            className={cx(
              TypographyStyle({
                color: "primary/60",
                ellipsis: true,
                fontSize: "14px",
                lineHeight: 1,
              }),
              pictogram ? css({ display: "block" }) : css({ display: "none" }),
            )}
          >
            {status?.state
              ? translate(
                  "%ResidentState." +
                    (easy
                      ? status.state === RESIDENT_STATE.WANDERING
                        ? RESIDENT_STATE.GOT_UP
                        : status.state
                      : status.state) +
                    ".0%",
                )
              : null}
          </span>
          <LuChevronRight
            className={cx(
              css({ height: "16px", width: "auto" }),
              !residentQuery.data && css({ display: "none" }),
            )}
          />
        </div>
        <div
          className={cx(
            css({ height: "72px", width: "72px" }),
            pictogram ? css({ display: "block" }) : css({ display: "none" }),
          )}
        >
          {pictogram}
        </div>
      </div>

      <div
        className={css(TypographyStyle.raw({ fontSize: "14px", fontWeight: 500, lineHeight: 1 }), {
          alignItems: "center",
          borderRadius: "8px",
          color: "white",
          display: "flex",
          gap: "8px",
          gridColumn: "1 / -1",
          padding: "6px 8px",
          width: "stretch",
        })}
        style={{
          backgroundColor:
            status?.state === RESIDENT_STATE.WANDERING ? "#18181B" : fromFallRiskToColor(fallRisk),
          display: alerted ? undefined : "none",
        }}
      >
        <LuMegaphone className={css({ height: "24px", width: "auto" })} />
        <p className={css({ flexGrow: 1 })}>
          {translate("%ResidentState." + (status?.state ?? RESIDENT_STATE.UNKNOWN) + ".2%")}
        </p>
        <p>{translate("%Time.2%", { $: status?.transitionedAt ?? now })}</p>
      </div>
    </div>
  );
}
